import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { motion } from 'framer-motion';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { prefersReducedMotion, pressable, SPRING, type MotionSafeProps } from '@/lib/motion';
import { toggleVariants } from '@/components/ui/toggle';

/*
 * Restyled to ScreenAtlas tokens (web/docs/ui-ux.md §5): the pill segmented
 * control for filter/sort within one surface — a recessed track (`recessed`
 * fill + a `border` hairline) with a raised thumb (`surface-raised` + the
 * inset highlight, ui-ux.md §2 "Material") — the one pill in the system. Items default to the
 * `segmented` variant; chip rows use <Toggle> directly instead.
 *
 * `variant="ink"` adds the gliding ink thumb (ui-ux.md §4): the active item's
 * label inverts to `text-bg` over a transparent fill while an `aria-hidden`
 * `motion.span` thumb glides behind it on `SPRING.thumb`. The thumb's layoutId
 * is derived per-instance from `React.useId()`, never a hardcoded string, so
 * two controls on one screen never collide. Reduced motion omits the layoutId
 * so the thumb snaps instead of gliding.
 */

type ToggleGroupVariant = NonNullable<VariantProps<typeof toggleVariants>['variant']>;
type ToggleGroupSize = VariantProps<typeof toggleVariants>['size'];

interface ToggleGroupContextValue {
  variant: ToggleGroupVariant;
  size: ToggleGroupSize;
  /** Instance-unique layoutId for the gliding ink thumb (`variant="ink"`). */
  thumbLayoutId?: string;
  /** The group's selection mode + value, used to locate the "on" item. */
  groupType?: 'single' | 'multiple';
  groupValue?: string | string[];
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue>({
  variant: 'segmented',
  size: 'default',
});

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => {
  const id = React.useId();
  const reduce = prefersReducedMotion();
  const resolvedVariant: ToggleGroupVariant = variant ?? 'segmented';
  const thumbLayoutId =
    resolvedVariant === 'ink' && !reduce ? `toggle-group-thumb-${id}` : undefined;

  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-border bg-recessed p-1',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider
        value={{
          variant: resolvedVariant,
          size,
          thumbLayoutId,
          groupType: props.type,
          groupValue: props.value,
        }}
      >
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const MotionToggleGroupItem = motion.create(ToggleGroupPrimitive.Item);

type ToggleGroupItemProps = MotionSafeProps<
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>
> &
  VariantProps<typeof toggleVariants>;

function isItemActive(
  groupType: 'single' | 'multiple' | undefined,
  groupValue: string | string[] | undefined,
  itemValue: string,
): boolean {
  if (groupValue === undefined) return false;
  if (groupType === 'multiple') {
    return Array.isArray(groupValue) && groupValue.includes(itemValue);
  }
  return groupValue === itemValue;
}

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  ToggleGroupItemProps
>(({ className, children, variant, size, value, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext);
  const isInk = context.variant === 'ink';
  const isActive = isInk && isItemActive(context.groupType, context.groupValue, value);

  return (
    <MotionToggleGroupItem
      ref={ref}
      value={value}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className,
      )}
      {...pressable()}
      {...props}
    >
      {isActive ? (
        <motion.span
          layoutId={context.thumbLayoutId}
          transition={SPRING.thumb}
          className="pointer-events-none absolute inset-0 rounded-full bg-ink"
          aria-hidden
        />
      ) : null}
      {isInk ? <span className="relative">{children}</span> : children}
    </MotionToggleGroupItem>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
